import { useEffect, useState } from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { CustomInput } from "global/components";
import { AppModal } from "global/components/AppModal";
import AccountMemberPicker from "global/components/AccountMemberPicker/AccountMemberPicker";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import {
  fetchSharesForRequest,
  revokeShare,
  shareRequest,
} from "screens/MySignRequests/DocumentSharingServices";

interface Props {
  /** The request being shared (null = dialog closed). */
  request: { id: string; title?: string } | null;
  onClose: () => void;
  onShared?: () => void;
}

/**
 * Share a sign request with account users (read-only), and manage who it's already shared with.
 * Adding a grant keeps the dialog open so the "Shared with" list updates and can be revoked. The
 * recipient field is the same type-ahead over account users used elsewhere.
 */
const ShareRequestDialog = ({ request, onClose, onShared }: Props) => {
  const isOpen = Boolean(request);
  const [grantee, setGrantee] = useState("");
  const [granteeValid, setGranteeValid] = useState(false);
  const [expiresOn, setExpiresOn] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shares, setShares] = useState<any[]>([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [revokingId, setRevokingId] = useState("");

  const loadShares = async () => {
    if (!request?.id) return;
    try {
      setSharesLoading(true);
      const s = await fetchSharesForRequest(request.id);
      setShares(Array.isArray(s) ? s : []);
    } catch {
      setShares([]);
    } finally {
      setSharesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setGrantee("");
      setGranteeValid(false);
      setExpiresOn("");
      setShares([]);
      loadShares();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, request?.id]);

  const submit = async () => {
    const to = grantee.trim();
    if (!request?.id || !to) return;
    try {
      setSubmitting(true);
      // Backend expiresOn is a LocalDateTime; a date-only pick means "through the end of that day".
      await shareRequest(request.id, to, expiresOn ? `${expiresOn}T23:59:59` : null);
      openSuccessNotification(`Shared with ${to}.`);
      setGrantee("");
      setExpiresOn("");
      onShared?.();
      await loadShares();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (grantId: string) => {
    try {
      setRevokingId(grantId);
      await revokeShare(grantId);
      onShared?.();
      await loadShares();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setRevokingId("");
    }
  };

  const expiryLabel = (iso?: string | null) => {
    if (!iso) return "";
    const d = String(iso).slice(0, 10);
    return d ? ` · expires ${d}` : "";
  };

  return (
    <AppModal
      open={isOpen}
      onClose={onClose}
      title="Share request"
      size="sm"
      cancelLabel="Close"
      onCancel={onClose}
    >
        <Stack spacing={2} sx={{ minWidth: 380, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Give another account user read-only access to this request. They'll see it under
            "Shared with me".
          </Typography>
          <AccountMemberPicker
            id="share-grantee"
            label="Share with"
            value={grantee}
            onChange={setGrantee}
            onValidityChange={setGranteeValid}
          />
          <CustomInput
            label="Expires on (optional)"
            type="date"
            name="expiresOn"
            value={expiresOn}
            onChange={(e: any) => setExpiresOn(e.target.value)}
            propsToInputElement={{ min: new Date().toISOString().slice(0, 10) }}
          />
          <Box>
            <Button
              variant="contained"
              onClick={submit}
              // Only a real, searched account member may be granted access (validity comes from
              // AccountMemberPicker) — never an arbitrary/typed string.
              disabled={submitting || !granteeValid}
            >
              {submitting ? "Sharing..." : "Share"}
            </Button>
          </Box>

          <Divider />
          <Typography variant="subtitle2">Shared with</Typography>
          {sharesLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          ) : shares.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Not shared with anyone yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {shares.map((g: any) => (
                <Stack
                  key={g.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2">
                    {g.granteeEmail}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {expiryLabel(g.expiresOn)}
                    </Typography>
                  </Typography>
                  <Button
                    size="small"
                    color="warning"
                    disabled={revokingId === g.id}
                    onClick={() => revoke(g.id)}
                  >
                    {revokingId === g.id ? "Removing…" : "Remove"}
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
    </AppModal>
  );
};

export default ShareRequestDialog;
